import { useAuthContext } from "@asgardeo/auth-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getGroupValues, hasAllowedGroup } from "../../auth/groupClaims";
import AccessDenied from "../../pages/AccessDenied/AccessDenied";

const parseAllowedGroups = (value) =>
  value
    ? value
        .split(",")
        .map((group) => group.trim())
        .filter(Boolean)
    : [];

const GroupGuard = ({ children }) => {
  const { state, getBasicUserInfo, getDecodedIDToken, signOut } =
    useAuthContext();
  const [authorized, setAuthorized] = useState(null);
  const hasStartedSignOut = useRef(false);

  const groupAuthEnabled = import.meta.env.VITE_GROUP_AUTH_ENABLED === "true";
  const allowedGroups = useMemo(
    () => parseAllowedGroups(import.meta.env.VITE_ALLOWED_GROUPS),
    []
  );

  useEffect(() => {
    if (!groupAuthEnabled) {
      setAuthorized(true);
      return;
    }

    if (state.isLoading) {
      return;
    }

    if (!state.isAuthenticated) {
      setAuthorized(null);
      return;
    }

    let isMounted = true;

    Promise.all([getDecodedIDToken(), getBasicUserInfo()])
      .then(([idTokenClaims, user]) => {
        if (!isMounted) {
          return;
        }

        const groups = getGroupValues({
          groupsClaimPath: "groups",
          idTokenClaims,
          user,
        });

        setAuthorized(hasAllowedGroup({ allowedGroups, groups }));
      })
      .catch(() => {
        if (isMounted) {
          setAuthorized(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    allowedGroups,
    getBasicUserInfo,
    getDecodedIDToken,
    groupAuthEnabled,
    state.isAuthenticated,
    state.isLoading,
  ]);

  useEffect(() => {
    if (!groupAuthEnabled || authorized !== false || hasStartedSignOut.current) {
      return;
    }

    hasStartedSignOut.current = true;
    signOut();
  }, [authorized, groupAuthEnabled, signOut]);

  if (!groupAuthEnabled) {
    return children;
  }

  if (state.isLoading || authorized === null) {
    return null;
  }

  if (!authorized) {
    return <AccessDenied />;
  }

  return children;
};

export default GroupGuard;
